import { Link, NavLink, useLocation } from "react-router-dom";
import { useState } from "react";
import { Menu, X, ArrowUpRight, ArrowRight, PhoneCall, Mail } from "lucide-react";
import { Logo } from "../Logo";
import { useSettings } from "../../context/SettingsContext";

const NAV = [
  { to: "/what-is-sellvago", label: "What is Sellvago" },
  { to: "/solutions", label: "Solutions" },
  { to: "/white-label-portal", label: "White-label" },
  { to: "/affiliate-program", label: "Affiliates" },
  { to: "/faq", label: "FAQs" },
];

export const B2BLayout = ({ children }) => {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const { settings } = useSettings();
  const signin = settings?.portal_signin_url || "https://partners.sellvago.com";

  return (
    <div className="b2b-page b2b-grain min-h-screen relative">
      <header className="fixed top-0 inset-x-0 z-50">
        <div className="bg-[#970C10] text-white" data-testid="b2b-support-bar">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 h-9 flex items-center justify-between text-xs">
            <p className="hidden sm:block">Smart solution for travel agents – freelancers and corporate</p>
            <div className="flex items-center gap-5 ml-auto">
              {settings?.support_phone && (
                <a href={`tel:${settings.support_phone}`} data-testid="b2b-support-phone" className="flex items-center gap-1.5 hover:text-white/80 transition-colors duration-200">
                  <PhoneCall className="w-3.5 h-3.5" /> 24/7 Support: {settings.support_phone}
                </a>
              )}
              {settings?.support_email && (
                <a href={`mailto:${settings.support_email}`} data-testid="b2b-support-email" className="hidden md:flex items-center gap-1.5 hover:text-white/80 transition-colors duration-200">
                  <Mail className="w-3.5 h-3.5" /> {settings.support_email}
                </a>
              )}
            </div>
          </div>
        </div>
        <div className="bg-white border-b border-slate-200">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
            <Link to="/" data-testid="b2b-logo-link">
              <Logo variant="secondary" tone="dark" height={36} />
            </Link>
            <nav className="hidden md:flex items-center gap-8">
              {NAV.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  data-testid={`b2b-nav-${n.label.toLowerCase().replace(/[^a-z]/g, "")}`}
                  className={({ isActive }) =>
                    `text-sm font-medium transition-colors duration-200 ${isActive ? "text-[#970C10]" : "text-slate-700 hover:text-[#970C10]"}`
                  }
                >
                  {n.label}
                </NavLink>
              ))}
              <Link
                to="/flights"
                data-testid="b2b-nav-flights-link"
                className="text-sm font-medium text-slate-700 hover:text-[#970C10] transition-colors duration-200 flex items-center gap-1"
              >
                Flight Deals <ArrowUpRight className="w-3.5 h-3.5" />
              </Link>
            </nav>
            <div className="hidden md:flex items-center gap-3">
              <a
                href={signin}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="b2b-signin-btn"
                className="text-sm font-semibold text-[#970C10] border border-[#970C10]/30 hover:bg-[#970C10]/5 px-5 py-2.5 rounded-sm transition-colors duration-200"
              >
                Sign In
              </a>
              <Link
                to="/signup"
                data-testid="b2b-signup-btn"
                className="bg-[#970C10] hover:bg-[#7A0A0D] text-white text-sm font-semibold px-5 py-2.5 rounded-sm transition-colors duration-200 flex items-center gap-2"
              >
                Sign Up <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
            <button
              data-testid="b2b-mobile-menu-btn"
              className="md:hidden text-slate-900"
              onClick={() => setOpen(!open)}
              aria-label="Menu"
            >
              {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
          {open && (
            <div className="md:hidden bg-white border-t border-slate-200 px-6 py-4 space-y-3" data-testid="b2b-mobile-menu">
              {[...NAV, { to: "/contact", label: "Contact Sales" }, { to: "/flights", label: "Flight Deals" }].map((n) => (
                <Link key={n.to} to={n.to} onClick={() => setOpen(false)} className="block text-slate-700 hover:text-[#970C10] py-1">
                  {n.label}
                </Link>
              ))}
              <div className="flex gap-3 pt-2">
                <a href={signin} target="_blank" rel="noopener noreferrer" className="flex-1 text-center text-sm font-semibold text-[#970C10] border border-[#970C10]/30 px-4 py-2.5 rounded-sm">
                  Sign In
                </a>
                <Link to="/signup" onClick={() => setOpen(false)} className="flex-1 text-center bg-[#970C10] text-white text-sm font-semibold px-4 py-2.5 rounded-sm">
                  Sign Up
                </Link>
              </div>
            </div>
          )}
        </div>
      </header>
      <main key={location.pathname} className="relative z-10">{children}</main>
      <footer className="relative z-10 border-t border-white/10 mt-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 grid md:grid-cols-4 gap-12">
          <div className="md:col-span-2">
            <div className="mb-4">
              <Logo variant="primary" tone="light" height={56} />
            </div>
            <p className="text-sm text-slate-400 max-w-sm leading-relaxed">
              The selling platform behind independent travel resellers — a content and technology platform that
              enables resellers to independently service travellers under their own brand name.
            </p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-4">Learn</p>
            <div className="space-y-2">
              <Link to="/what-is-sellvago" data-testid="b2b-footer-what-is" className="block text-sm text-slate-400 hover:text-white transition-colors duration-200">What is Sellvago</Link>
              <Link to="/sellvago-vs-b2b-ota" data-testid="b2b-footer-vs-ota" className="block text-sm text-slate-400 hover:text-white transition-colors duration-200">Sellvago vs B2B OTA</Link>
              <Link to="/how-white-label-works" data-testid="b2b-footer-how-wl" className="block text-sm text-slate-400 hover:text-white transition-colors duration-200">How white-label works</Link>
              <Link to="/how-resellers-grow" data-testid="b2b-footer-how-grow" className="block text-sm text-slate-400 hover:text-white transition-colors duration-200">How resellers grow</Link>
            </div>
          </div>
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-4">Company</p>
            <div className="space-y-2">
              <Link to="/about" className="block text-sm text-slate-400 hover:text-white transition-colors duration-200">About</Link>
              <Link to="/solutions" className="block text-sm text-slate-400 hover:text-white transition-colors duration-200">Solutions</Link>
              <Link to="/contact" className="block text-sm text-slate-400 hover:text-white transition-colors duration-200">Contact Sales</Link>
              <Link to="/faq" className="block text-sm text-slate-400 hover:text-white transition-colors duration-200">FAQs</Link>
              <Link to="/flights" className="block text-sm text-slate-400 hover:text-white transition-colors duration-200">Farevault — Flight Deals</Link>
            </div>
          </div>
        </div>
        <div className="border-t border-white/10 py-6">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-slate-600">
            <p>© {new Date().getFullYear()} Sellvago. All rights reserved.</p>
            <div className="flex items-center gap-5">
              <Link to="/privacy" data-testid="b2b-footer-privacy" className="hover:text-white transition-colors duration-200">Privacy</Link>
              <Link to="/terms" data-testid="b2b-footer-terms" className="hover:text-white transition-colors duration-200">Terms</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};
